import { useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "@/App";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Download, RotateCcw, Eye, CreditCard, User, Trash2, Edit3, Plus, Loader2, Save } from "lucide-react";
import { toast } from "sonner";

const CARD_W = 1050;
const CARD_H = 600;
const SCALE = 0.42;
const NAVY = "#2B4C7E";
const MED_BLUE = "#4A7DB5";
const LIGHT_BLUE = "#6B8EB5";

const BACK_IMG_URL = `${API}/static/business_card_back.jpg`;

const EMPTY_FIELDS = {
  name: "", title: "", subtitle: "", phone: "",
  email: "", address_line1: "", address_line2: "",
};

/* ─── Draw front — matches uploaded design ─── */
function drawFront(ctx, f) {
  const w = CARD_W, h = CARD_H;
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, w, h);

  const mx = 64; // left margin
  ctx.textBaseline = "top";

  // Split name into parts for stacked display
  const parts = (f.name || "Full Name").trim().split(/\s+/);
  ctx.fillStyle = NAVY;
  ctx.font = "bold 78px 'Helvetica Neue', Helvetica, Arial, sans-serif";
  let ny = 50;
  parts.forEach((word) => {
    ctx.fillText(word, mx, ny);
    ny += 88;
  });

  // Title
  ctx.fillStyle = MED_BLUE;
  ctx.font = "400 30px 'Helvetica Neue', Helvetica, Arial, sans-serif";
  ctx.fillText(f.title || "Job Title", mx, ny + 8);

  // Subtitle (optional)
  if (f.subtitle) {
    ctx.fillStyle = LIGHT_BLUE;
    ctx.font = "400 22px 'Helvetica Neue', Helvetica, Arial, sans-serif";
    ctx.fillText(f.subtitle, mx, ny + 48);
  }

  // Bottom left — phone + email (moved up)
  const by = h - 108;
  ctx.fillStyle = NAVY;
  ctx.font = "500 24px 'Helvetica Neue', Helvetica, Arial, sans-serif";
  if (f.phone) ctx.fillText(f.phone, mx, by);
  ctx.fillStyle = MED_BLUE;
  ctx.font = "400 22px 'Helvetica Neue', Helvetica, Arial, sans-serif";
  if (f.email) ctx.fillText(f.email, mx, by + 34);

  // Bottom right — address (moved up)
  ctx.textAlign = "right";
  ctx.fillStyle = NAVY;
  ctx.font = "400 22px 'Helvetica Neue', Helvetica, Arial, sans-serif";
  if (f.address_line1) ctx.fillText(f.address_line1, w - mx, by);
  ctx.fillStyle = MED_BLUE;
  if (f.address_line2) ctx.fillText(f.address_line2, w - mx, by + 30);
  ctx.textAlign = "left";
}

/* ─── Card Canvas ─── */
function CardCanvas({ fields, side, backImg }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    c.width = CARD_W;
    c.height = CARD_H;
    const ctx = c.getContext("2d");
    if (side === "front") {
      drawFront(ctx, fields);
    } else if (backImg) {
      ctx.drawImage(backImg, 0, 0, CARD_W, CARD_H);
    } else {
      ctx.fillStyle = NAVY;
      ctx.fillRect(0, 0, CARD_W, CARD_H);
      ctx.fillStyle = "#fff";
      ctx.textAlign = "center";
      ctx.font = "bold 32px Helvetica, sans-serif";
      ctx.fillText("Enable Healthcare", CARD_W / 2, CARD_H / 2);
      ctx.textAlign = "left";
    }
  }, [fields, side, backImg]);

  return (
    <canvas
      ref={ref}
      style={{ width: CARD_W * SCALE, height: CARD_H * SCALE, borderRadius: 10, boxShadow: "0 6px 24px rgba(0,0,0,0.12)" }}
      data-testid={`card-canvas-${side}`}
    />
  );
}

/* ─── Saved Card Row ─── */
function SavedCardRow({ card, onEdit, onDelete, onDownload }) {
  return (
    <div
      className="flex items-center gap-3 p-3 rounded-xl bg-white transition-all hover:shadow-md"
      style={{ boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}
      data-testid={`saved-card-${card.id}`}
    >
      {/* Mini preview */}
      <div className="w-[90px] h-[52px] rounded-md overflow-hidden flex-shrink-0 bg-white border border-slate-100">
        <MiniPreview fields={card} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#1B365D] truncate">{card.name || "Untitled"}</p>
        <p className="text-[10px] text-slate-400 truncate">{card.title}{card.email ? ` — ${card.email}` : ""}</p>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button onClick={() => onEdit(card)} className="p-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid={`edit-card-${card.id}`}>
          <Edit3 className="h-3.5 w-3.5 text-slate-400" />
        </button>
        <button onClick={() => onDownload(card)} className="p-1.5 rounded-lg hover:bg-blue-50 transition-colors" data-testid={`download-card-${card.id}`}>
          <Download className="h-3.5 w-3.5 text-[#0071E3]" />
        </button>
        <button onClick={() => onDelete(card.id)} className="p-1.5 rounded-lg hover:bg-red-50 transition-colors" data-testid={`delete-card-${card.id}`}>
          <Trash2 className="h-3.5 w-3.5 text-red-400" />
        </button>
      </div>
    </div>
  );
}

/* ─── Mini front preview (for saved list) ─── */
function MiniPreview({ fields }) {
  const ref = useRef(null);
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    c.width = CARD_W;
    c.height = CARD_H;
    drawFront(c.getContext("2d"), fields);
  }, [fields]);
  return <canvas ref={ref} style={{ width: "100%", height: "100%" }} />;
}

/* ─── Main Component ─── */
export default function BusinessCardGenerator() {
  const { token } = useAuth();
  const [fields, setFields] = useState(EMPTY_FIELDS);
  const [activeSide, setActiveSide] = useState("front");
  const [backImg, setBackImg] = useState(null);
  const [savedCards, setSavedCards] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const update = (key, val) => setFields(p => ({ ...p, [key]: val }));

  // Load back image
  useEffect(() => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => setBackImg(img);
    img.src = BACK_IMG_URL;
  }, []);

  // Load saved cards
  // eslint-disable-next-line react-hooks/exhaustive-deps -- fetchCards defined below; run once on mount
  useEffect(() => { fetchCards(); }, []);

  const fetchCards = async () => {
    try {
      const res = await axios.get(`${API}/business-cards`, { headers: { Authorization: `Bearer ${token}` } });
      setSavedCards(res.data.cards || []);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const saveCard = async () => {
    if (!fields.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/business-cards/${editingId}`, fields, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Card updated");
      } else {
        await axios.post(`${API}/business-cards`, fields, { headers: { Authorization: `Bearer ${token}` } });
        toast.success("Card saved");
      }
      setEditingId(null);
      fetchCards();
    } catch { toast.error("Failed to save"); }
    setSaving(false);
  };

  const deleteCard = async (id) => {
    try {
      await axios.delete(`${API}/business-cards/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSavedCards(p => p.filter(c => c.id !== id));
      if (editingId === id) { setEditingId(null); setFields(EMPTY_FIELDS); }
      toast.success("Card deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const editCard = (card) => {
    setFields({
      name: card.name || "",
      title: card.title || "",
      subtitle: card.subtitle || "",
      phone: card.phone || "",
      email: card.email || "",
      address_line1: card.address_line1 || "",
      address_line2: card.address_line2 || "",
    });
    setEditingId(card.id);
    setActiveSide("front");
  };

  const newCard = () => {
    setFields(EMPTY_FIELDS);
    setEditingId(null);
  };

  const downloadCard = useCallback((side, f = fields) => {
    const canvas = document.createElement("canvas");
    canvas.width = CARD_W;
    canvas.height = CARD_H;
    const ctx = canvas.getContext("2d");
    if (side === "front") {
      drawFront(ctx, f);
      exportCanvas(canvas, `enable_card_front_${(f.name || "card").replace(/\s+/g, "_")}.png`);
    } else if (backImg) {
      ctx.drawImage(backImg, 0, 0, CARD_W, CARD_H);
      exportCanvas(canvas, "enable_card_back.png");
    }
  }, [fields, backImg]);

  const exportCanvas = (canvas, filename) => {
    canvas.toBlob((blob) => {
      if (!blob) return;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    }, "image/png");
  };

  const downloadBoth = useCallback((f = fields) => {
    downloadCard("front", f);
    setTimeout(() => downloadCard("back", f), 400);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `fields` is the default arg; caller passes `f` explicitly
  }, [downloadCard]);

  return (
    <div data-testid="business-card-generator">
      {/* Header */}
      <div className="rounded-2xl p-5 mb-6 text-white" style={{ background: "linear-gradient(135deg, #0A1628 0%, #1B365D 50%, #0071E3 100%)" }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
              <CreditCard className="h-5 w-5 text-white" />
            </div>
            <div>
              <h3 className="font-bold text-white text-base">Business Card Generator</h3>
              <p className="text-[12px] text-white/70">Create print-ready Enable Healthcare business cards at 300 DPI</p>
            </div>
          </div>
          <Badge className="bg-white/15 text-white/80 text-[10px]">{savedCards.length} saved</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        {/* Left column: Form + Saved */}
        <div className="lg:col-span-5 space-y-4">
          {/* Form */}
          <div className="bg-white rounded-2xl p-5" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-[#0071E3]" />
                <h4 className="font-semibold text-[#1B365D] text-sm">{editingId ? "Edit Card" : "New Card"}</h4>
              </div>
              {editingId && (
                <button onClick={newCard} className="text-[10px] font-semibold text-[#0071E3] flex items-center gap-1 hover:underline" data-testid="new-card-btn">
                  <Plus className="h-3 w-3" /> New
                </button>
              )}
            </div>
            <div className="space-y-2.5">
              <div>
                <Label className="text-[10px] text-slate-400 mb-0.5 block">Full Name</Label>
                <Input value={fields.name} onChange={e => update("name", e.target.value)} placeholder="John Smith" className="rounded-lg h-9 text-sm" data-testid="card-name-input" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400 mb-0.5 block">Title</Label>
                <Input value={fields.title} onChange={e => update("title", e.target.value)} placeholder="Sales Representative" className="rounded-lg h-9 text-sm" data-testid="card-title-input" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400 mb-0.5 block">Subtitle / Department (optional)</Label>
                <Input value={fields.subtitle} onChange={e => update("subtitle", e.target.value)} placeholder="Sales & Business Development" className="rounded-lg h-9 text-sm" data-testid="card-subtitle-input" />
              </div>
              <div className="grid grid-cols-2 gap-2.5">
                <div>
                  <Label className="text-[10px] text-slate-400 mb-0.5 block">Phone</Label>
                  <Input value={fields.phone} onChange={e => update("phone", e.target.value)} placeholder="(555) 123-4567" className="rounded-lg h-9 text-sm" data-testid="card-phone-input" />
                </div>
                <div>
                  <Label className="text-[10px] text-slate-400 mb-0.5 block">Email</Label>
                  <Input value={fields.email} onChange={e => update("email", e.target.value)} placeholder="jsmith@ehiehr.com" className="rounded-lg h-9 text-sm" data-testid="card-email-input" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] text-slate-400 mb-0.5 block">Address Line 1</Label>
                <Input value={fields.address_line1} onChange={e => update("address_line1", e.target.value)} placeholder="100 Eagle Rock Avenue, Suite 304" className="rounded-lg h-9 text-sm" data-testid="card-address1-input" />
              </div>
              <div>
                <Label className="text-[10px] text-slate-400 mb-0.5 block">Address Line 2</Label>
                <Input value={fields.address_line2} onChange={e => update("address_line2", e.target.value)} placeholder="East Hanover, NJ 07936" className="rounded-lg h-9 text-sm" data-testid="card-address2-input" />
              </div>
            </div>

            <div className="flex gap-2 mt-4">
              <Button onClick={saveCard} disabled={saving} className="flex-1 rounded-xl bg-[#1B365D] hover:bg-[#142846] text-white h-9 text-xs" data-testid="save-card-btn">
                {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Save className="h-3 w-3 mr-1" />}
                {editingId ? "Update" : "Save"} Card
              </Button>
              <Button onClick={() => downloadBoth()} className="flex-1 rounded-xl bg-[#0071E3] hover:bg-[#005bb5] text-white h-9 text-xs" data-testid="download-both-btn">
                <Download className="h-3 w-3 mr-1" /> Download
              </Button>
              <Button variant="ghost" onClick={newCard} className="rounded-xl text-slate-400 h-9 px-2" data-testid="reset-btn">
                <RotateCcw className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>

          {/* Saved Cards */}
          <div className="bg-white rounded-2xl p-5" style={{ boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }}>
            <div className="flex items-center justify-between mb-3">
              <h4 className="font-semibold text-[#1B365D] text-sm">Saved Cards</h4>
              <Badge variant="outline" className="text-[10px]">{savedCards.length}</Badge>
            </div>
            {loading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="h-4 w-4 animate-spin text-[#0071E3]" />
              </div>
            ) : savedCards.length === 0 ? (
              <div className="text-center py-6 text-slate-400">
                <CreditCard className="h-6 w-6 mx-auto mb-2 text-slate-300" />
                <p className="text-xs">No saved cards yet</p>
              </div>
            ) : (
              <div className="space-y-2" data-testid="saved-cards-list">
                {savedCards.map(card => (
                  <SavedCardRow
                    key={card.id}
                    card={card}
                    onEdit={editCard}
                    onDelete={deleteCard}
                    onDownload={(c) => downloadBoth(c)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Right column: Live Preview */}
        <div className="lg:col-span-7">
          <div className="flex gap-1.5 mb-4">
            {[
              { id: "front", label: "Front", icon: Eye },
              { id: "back", label: "Back", icon: CreditCard },
            ].map(s => (
              <button
                key={s.id}
                onClick={() => setActiveSide(s.id)}
                className={`px-5 py-2 rounded-full text-xs font-medium transition-all duration-200 flex items-center gap-1.5 ${
                  activeSide === s.id ? "bg-[#1B365D] text-white shadow-sm" : "bg-white text-slate-500"
                }`}
                style={activeSide !== s.id ? { boxShadow: "0 1px 3px rgba(0,0,0,0.06)" } : {}}
                data-testid={`preview-toggle-${s.id}`}
              >
                <s.icon className="h-3.5 w-3.5" /> {s.label}
              </button>
            ))}
          </div>

          <motion.div
            key={activeSide}
            initial={{ opacity: 0, rotateY: 90 }}
            animate={{ opacity: 1, rotateY: 0 }}
            transition={{ duration: 0.3 }}
            className="flex justify-center"
          >
            <CardCanvas fields={fields} side={activeSide} backImg={backImg} />
          </motion.div>

          <div className="mt-3 flex items-center justify-center gap-3">
            <Badge variant="outline" className="text-[10px] text-slate-400">3.5 x 2 in at 300 DPI</Badge>
            <div className="flex gap-1.5">
              <button onClick={() => downloadCard("front")} className="text-[10px] font-semibold text-[#0071E3] hover:underline flex items-center gap-0.5" data-testid="download-front-btn">
                <Download className="h-3 w-3" /> Front
              </button>
              <span className="text-slate-300">|</span>
              <button onClick={() => downloadCard("back")} className="text-[10px] font-semibold text-[#0071E3] hover:underline flex items-center gap-0.5" data-testid="download-back-btn">
                <Download className="h-3 w-3" /> Back
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
